/**
 * SettingsChangeNotifier — Global listener for settings change events.
 * Shows a toast.info notification when entity types, module states, users, or task columns
 * are updated from the Settings or Users pages, so users in other modules are informed.
 *
 * Mount once at the app level (e.g., AdminLayout).
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";

const EVENT_CONFIG = {
  "entity-types-updated": {
    getMsg: (detail) =>
      detail?.label
        ? `Nový typ "${detail.label}" přidán v Nastavení — dostupný v dropdownech`
        : "Typy entit aktualizovány v Nastavení",
    skipPaths: ["/admin/settings"],
  },
  "module-states-updated": {
    getMsg: (detail) =>
      detail?.label
        ? `Stav "${detail.label}" přidán v Nastavení — dostupný v celé aplikaci`
        : "Stavový automat aktualizován v Nastavení",
    skipPaths: ["/admin/settings"],
  },
  "users-updated": {
    getMsg: (detail) =>
      detail?.label && detail?.action === "created"
        ? `Nový uživatel "${detail.label}" přidán`
        : detail?.label && detail?.action === "deleted"
        ? `Uživatel "${detail.label}" odebrán`
        : null, // no toast for generic update
    skipPaths: ["/admin/users"],
  },
  "task-columns-updated": {
    getMsg: (detail) =>
      detail?.label
        ? `Sloupec úkolů "${detail.label}" aktualizován`
        : "Sloupce úkolů aktualizovány v Nastavení",
    skipPaths: ["/admin/tasks"],
  },
};

// Deduplicate: suppress identical toasts within 1.5 s
const lastToast = {};

function shouldShow(eventName, msg, currentPath, skipPaths) {
  if (!msg) return false;
  if (skipPaths.some((p) => currentPath.startsWith(p))) return false;
  const now = Date.now();
  if (lastToast[eventName] && now - lastToast[eventName] < 1500) return false;
  lastToast[eventName] = now;
  return true;
}

export default function SettingsChangeNotifier() {
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

  useEffect(() => {
    const handlers = {};
    for (const [eventName, cfg] of Object.entries(EVENT_CONFIG)) {
      handlers[eventName] = (e) => {
        const msg = cfg.getMsg(e.detail || {});
        if (shouldShow(eventName, msg, pathRef.current, cfg.skipPaths)) {
          toast.info(msg, { duration: 4000 });
        }
      };
      window.addEventListener(eventName, handlers[eventName]);
    }
    return () => {
      for (const [eventName, handler] of Object.entries(handlers)) {
        window.removeEventListener(eventName, handler);
      }
    };
  }, []); // mount once

  return null;
}
